import { memo, useCallback, useState } from "react";
import { FormattedMessage } from "react-intl";

import { useNotaryMeetingContext } from "common/meeting/notary/context";
import { useMutation } from "util/graphql";
import Modal from "common/modal";

import Styles from "./form.module.scss";
import CollectNotaryColocatedWitnessInformation, {
  type NotaryColocatedInformationFormValues,
} from "./notary_colocated";
import AttestNotaryColocatedWitness, { type AttestFormValues } from "./attest";
import CollectFullyRemoteWitnessInformation, {
  type FormValues as FullyRemoteWitnessFormValues,
} from "./fully_remote";
import {
  SignerAlertConfirmation,
  ChooseSignerPartyForWitness,
  type FormValues as ChooseSignerPartyFormValues,
} from "./with_signer";
import NotarySelfWitnessConfirm, { SELF_WITNESS_RELATIONSHIP } from "./notary_self_witness";
import ChooseWitnessKind, {
  type FormValues as ChooseWitnessKindFormValues,
} from "./choose_witness_kind";
import CreateWitnessMutation, {
  type CreateWitnessVariables,
} from "./create_witness_mutation.graphql";
import type {
  NotaryAddWitness as Meeting,
  NotaryAddWitness_meetingParticipants as MeetingParticipant,
} from "./index_fragment.graphql";

type Step = Readonly<
  | { type: "choose-witness-kind" }
  | { type: "notary-self-witness-confirm" }
  | { type: "choose-signer-party-for-witness"; primarySignerParticipants: MeetingParticipant[] }
  | { type: "signer-alert-confirm" }
  | { type: "fully-remote-witness-collect-information" }
  | { type: "notary-colocated-witness-collect-information" }
  | {
      type: "notary-colocated-attest-known";
      collectedInformation: NotaryColocatedInformationFormValues;
    }
>;
type Props = {
  meeting: Meeting;
  notaryUser: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    notaryProfile: null | {
      address: CreateWitnessVariables["input"]["address"] | null;
    };
  };
  onClose: () => void;
};

const INITIAL_STEP: Step = Object.freeze({ type: "choose-witness-kind" });

function AddWitnessModal({ meeting, onClose, notaryUser }: Props) {
  const { id: meetingId, meetingParticipants } = meeting;
  const addWitnessMutateFn = useMutation(CreateWitnessMutation);
  const { channel, analytics } = useNotaryMeetingContext();
  const [step, setStep] = useState<Step>(INITIAL_STEP);
  const handleBackToBeginning = useCallback(() => setStep(INITIAL_STEP), []);
  const handleChooseSignerPartyForWitness = useCallback(
    ({ meetingParticipantId }: ChooseSignerPartyFormValues) => {
      channel.sendMessage("create_witness", { meeting_participant_id: meetingParticipantId });
      analytics.onAddWitness();
      setStep(Object.freeze({ type: "signer-alert-confirm" }));
    },
    [channel, analytics, onClose],
  );
  const handleChooseKindSubmit = useCallback(
    ({ kind }: ChooseWitnessKindFormValues) => {
      const isSignerColocated = kind === "signer-colocated";
      const primarySignerParticipants = meetingParticipants.filter(
        (p) => !p.parentId && p.__typename === "SignerParticipant",
      );
      if (isSignerColocated && primarySignerParticipants.length === 1) {
        return handleChooseSignerPartyForWitness({
          meetingParticipantId: primarySignerParticipants[0].id,
        });
      } else if (isSignerColocated) {
        return setStep(
          Object.freeze({ type: "choose-signer-party-for-witness", primarySignerParticipants }),
        );
      }

      const newStepType =
        kind === "fully-remote"
          ? "fully-remote-witness-collect-information"
          : kind === "notary-self-witness"
          ? "notary-self-witness-confirm"
          : "notary-colocated-witness-collect-information";
      setStep(Object.freeze({ type: newStepType }));
    },
    [meetingParticipants, handleChooseSignerPartyForWitness],
  );
  const handleSubmitNotaryColocatedAttest = useCallback(
    (formValues: AttestFormValues) => {
      if (step.type !== "notary-colocated-attest-known") {
        throw new Error("Unexpected ");
      }
      const { collectedInformation } = step;
      const input = {
        firstName: collectedInformation.firstName,
        lastName: collectedInformation.lastName,
        meetingId,
        address: collectedInformation.address,
        relationship: formValues.relationship,
      };
      return addWitnessMutateFn({ variables: { input } }).then(onClose);
    },
    [step, addWitnessMutateFn, meetingId, onClose],
  );
  const handleSubmitFullyRemoteWitness = useCallback(
    (formValues: FullyRemoteWitnessFormValues) => {
      const input = {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        email: formValues.email,
        meetingId,
      };
      return addWitnessMutateFn({ variables: { input } }).then(onClose);
    },
    [addWitnessMutateFn, meetingId, onClose],
  );
  const handleSubmitNotaryColocatedInformation = useCallback(
    (collectedInformation: NotaryColocatedInformationFormValues) => {
      setStep(Object.freeze({ collectedInformation, type: "notary-colocated-attest-known" }));
    },
    [],
  );
  const handleSubmitSelfWitnessConfirm = useCallback(() => {
    const notaryAddress = notaryUser.notaryProfile!.address!;
    const input = {
      firstName: notaryUser.firstName!,
      lastName: notaryUser.lastName!,
      meetingId,
      address: {
        line1: notaryAddress.line1,
        line2: notaryAddress.line2,
        city: notaryAddress.city,
        state: notaryAddress.state,
        postal: notaryAddress.postal,
        country: notaryAddress.country,
      },
      relationship: SELF_WITNESS_RELATIONSHIP,
    };
    return addWitnessMutateFn({ variables: { input } }).then(onClose);
  }, [notaryUser, meetingId, addWitnessMutateFn, onClose]);
  return (
    <Modal
      closeRoute={onClose}
      title={
        <FormattedMessage
          id="3da1a6ec-2291-4ba6-8d87-14de4a9f3601"
          defaultMessage="Witness Information"
        />
      }
    >
      <div className={Styles.witnessModalBody}>
        <div className={Styles.witnessModalWarning}>
          <FormattedMessage
            id="3da1a6ec-2291-4ba6-8d87-14de4a9f3603"
            defaultMessage="Note: Lock documents before the witness leaves to ensure signatures are not erased."
          />
        </div>

        {step.type === "choose-witness-kind" ? (
          <ChooseWitnessKind
            onSubmit={handleChooseKindSubmit}
            documentBundle={meeting.documentBundle!}
            meetingParticipants={meeting.meetingParticipants}
            notaryUserId={notaryUser.id}
          />
        ) : step.type === "notary-self-witness-confirm" ? (
          <NotarySelfWitnessConfirm
            onBack={handleBackToBeginning}
            onSubmit={handleSubmitSelfWitnessConfirm}
          />
        ) : step.type === "signer-alert-confirm" ? (
          <SignerAlertConfirmation onSubmit={onClose} />
        ) : step.type === "choose-signer-party-for-witness" ? (
          <ChooseSignerPartyForWitness
            onBack={handleBackToBeginning}
            onSubmit={handleChooseSignerPartyForWitness}
            primarySignerParticipants={step.primarySignerParticipants}
          />
        ) : step.type === "notary-colocated-witness-collect-information" ? (
          <CollectNotaryColocatedWitnessInformation
            onBack={handleBackToBeginning}
            onSubmit={handleSubmitNotaryColocatedInformation}
          />
        ) : step.type === "notary-colocated-attest-known" ? (
          <AttestNotaryColocatedWitness
            onBack={handleBackToBeginning}
            onSubmit={handleSubmitNotaryColocatedAttest}
            witnessInformation={step.collectedInformation}
            notaryUser={notaryUser}
          />
        ) : (
          // "fully-remote-witness-collect-information"
          <CollectFullyRemoteWitnessInformation
            onBack={handleBackToBeginning}
            onSubmit={handleSubmitFullyRemoteWitness}
          />
        )}
      </div>
    </Modal>
  );
}

export default memo(AddWitnessModal);
